import HtmlPageTitle from "components/HtmlPageTitle";
import React, { ReactNode, useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useBoundStore } from "@/store";
import type {
  AnalyticsEventsMap,
  AuthMethod,
  BillingInterval,
  Plan,
  PlanType,
} from "types/analytics";
import { addDateTz } from "pages/media/utils";
import { User } from "types";
import {
  getAnalytics,
  registerSuperProperties,
} from "@libertify.frontend/analytics";
import {
  getAttribution,
  hasUTMValues,
  restoreAttributionFromUrl,
} from "utils/analytics";
import type { OnboardingState, Step } from "pages/onboarding/types";

type UserProperties = {
  email?: string;
  first_name?: string;
  last_name?: string;
  country?: string;
  created?: string;
  organizations?: string[];
};
type AdditionalUserProperties = {
  plan?: Plan;
  plan_type?: PlanType;
  billing_interval?: BillingInterval;
  signup_method?: AuthMethod;
} & Record<string, string>;
type UserTraits = {
  email?: string;
  firstName?: string;
  lastName?: string;
  address?: { country?: string };
  createdAt?: string;
  signup_method?: AuthMethod;
  plan?: Plan;
  plan_type?: PlanType;
  billing_interval?: BillingInterval;
  organization_id?: string[];
  icp_type?: string;
};

/** Mirrors @segment/analytics-core Campaign (not re-exported from package root) */
type Campaign = { name: string; source: string; medium: string; [k: string]: string };

export const initializeAnalytics = () => {
  // Legacy key from the 30-day first touch, no longer read or expired
  // TODO(MED-7439): remove after 2026-12-15 (30 days after this reaches prod; move the date if the release slips)
  localStorage.removeItem("first_touch_utm_params");
  restoreAttributionFromUrl(globalThis.location.search);
};

const getUserCommonProperties = () => {
  const { utm: utmParams, icpType } = getAttribution();
  // persist initial utms at user level across sessions
  // TODO : track reengagement utm_xxx
  return {
    ...(icpType && { icp_type: icpType }),
    ...(hasUTMValues(utmParams)
      ? {
          // DLY hack (from Claude) : default to empty string to guard against set-once trying
          // to set undefined values from partial first touch utm params
          // simpler than only checking in the Segment functions or HubSpot sync set-once
          first_utm_campaign: utmParams.utm_campaign ?? "",
          first_utm_source: utmParams.utm_source ?? "",
          first_utm_medium: utmParams.utm_medium ?? "",
          first_utm_term: utmParams.utm_term ?? "",
          first_utm_content: utmParams.utm_content ?? "",
          first_utm_id: utmParams.utm_id ?? "",
          first_utm_touchpoint: new Date().toISOString(),
        }
      : null),
  };
};

export const getOnboardingProperties = (
  user?: { plan?: string },
  signupMethod?: "email" | "google" | "oauth"
) => {
  const onboardingProperties = JSON.parse(
    sessionStorage.getItem("onboarding_properties") || "{}"
  );
  const { icpType } = getAttribution();
  return {
    plan: user?.plan ? user.plan : onboardingProperties.plan || "Trial",
    signup_method: signupMethod ?? onboardingProperties.signup_method,
    ...(icpType && { icp_type: icpType }),
  };
};

type OnboardingProperties = {
  plan?: string;
  plan_type?: string;
  billing_interval?: string;
  signup_method?: AuthMethod;
};
// persist registration details in session to use
// in onboarding events until we can get them from the backend easily
export const persistOnboardingProperties = (
  properties: OnboardingProperties
) => {
  const currentProperties: Record<string, unknown> = JSON.parse(
    sessionStorage.getItem("onboarding_properties") || "{}"
  );

  // Only keep defined values to avoid clearing existing properties with undefined
  const propertiesToPersist = Object.fromEntries(
    Object.entries({
      plan: properties.plan,
      plan_type: properties.plan_type,
      billing_interval: properties.billing_interval,
      signup_method: properties.signup_method,
    }).filter(([, value]) => value !== undefined)
  );

  sessionStorage.setItem(
    "onboarding_properties",
    JSON.stringify({ ...currentProperties, ...propertiesToPersist })
  );
};

const getTrackingProperties = (
  additionalProps: Record<string, unknown> = {},
  organizationId?: string | null
) => ({
  ...(organizationId && { organization_id: organizationId }),
  ...additionalProps,
});

const getTrackingContext = () => {
  const campaign = getAttribution().utm;
  if (!hasUTMValues(campaign)) return {};

  // already auto added to page events by Segment lib when url contains utms url params
  // we need to add it to other events ourselves
  // then the values can be used in other third parties like Hubspot adapter, etc
  //
  // UTM params are inherently partial at runtime, but the Segment Campaign
  // type requires { name, source, medium } as non-optional strings plus a
  // string index signature. We keep only defined values and use a type
  // assertion because partial UTMs are valid for downstream consumers.
  return {
    campaign: Object.fromEntries(
      Object.entries({
        name: campaign.utm_campaign,
        source: campaign.utm_source,
        medium: campaign.utm_medium,
        term: campaign.utm_term,
        content: campaign.utm_content,
        id: campaign.utm_id,
      }).filter(([, v]) => v !== undefined)
    ) as unknown as Campaign,
  };
};

export const useAnalytics = () => {
  const organizationId = useBoundStore((state) => state.selectedOrganizationId);

  const baseTrack = useCallback(
    async (event: string, properties?: Record<string, unknown>) => {
      const analytics = await getAnalytics();
      const trackingProps = getTrackingProperties(properties, organizationId);
      const trackingContext = getTrackingContext();

      await analytics.track(event, trackingProps, {
        context: trackingContext,
      });
    },
    [organizationId]
  );

  const baseIdentify = useCallback(
    async (userId: string, traits?: Record<string, unknown>) => {
      const analytics = await getAnalytics();
      const userTraits = {
        ...traits,
        ...getUserCommonProperties(),
      };

      await analytics.identify(userId, userTraits, {
        context: getTrackingContext(),
      });
    },
    []
  );

  const baseGroup = useCallback(
    async (groupId: string, traits?: Record<string, unknown>) => {
      const analytics = await getAnalytics();
      const groupTraits = traits;

      await analytics.group(groupId, groupTraits, {
        context: getTrackingContext(),
      });
    },
    []
  );

  const basePage = useCallback(
    async (title?: string) => {
      const analytics = await getAnalytics();
      const urlParams = new URLSearchParams(globalThis.window.location.search);
      const analyticsTitle = document
        .querySelector('meta[name="analytics-title"]')
        ?.getAttribute("content");
      let anonymousId;

      try {
        // take Segment anonymousId from website links to keep the user session between
        // Segment Sources
        // https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/
        const aidParam = urlParams.get("aid");
        anonymousId = aidParam ? { anonymousId: atob(aidParam) } : null;
      } catch (e) {
        console.debug("Invalid anonymousId", e);
      }

      const trackingProps = getTrackingProperties(
        { ...anonymousId },
        organizationId
      );

      await analytics.page(undefined, analyticsTitle ?? title, trackingProps, {
        context: getTrackingContext(),
      });
    },
    [organizationId]
  );

  const trackPageView = useCallback(() => {
    // hack : wait page to be rendered with proper page title
    setTimeout(() => {
      basePage();
    }, 1000);
  }, [basePage]);

  const identifyUser = useCallback(
    (
      userId: string,
      userProperties?: UserProperties,
      additionalUserProperties?: AdditionalUserProperties
    ) => {
      // Tag internal traffic for GA4 filtering
      // currently using a super property for all Segment events and destinations
      // we can refactor with a cleaner custom middleware for Google Analytics 4 Web
      if (userProperties?.email?.endsWith("@libertify.com")) {
        registerSuperProperties({ traffic_type: "internal" });
      }

      // TODO add signup method when we ll be able to get it
      // from the backend easily even after user creation
      // https://segment.com/docs/connections/spec/identify/
      baseIdentify(
        userId,
        createUserTraits(userProperties, additionalUserProperties)
      ).catch((error) => {
        console.debug("[Analytics] Error identifying user", error);
      });
    },
    [baseIdentify]
  );

  const trackOnboardingStep = useCallback(
    (step: Step) => {
      baseTrack("onboarding_step_view", { step });
    },
    [baseTrack]
  );

  const trackOnboardingAnswer = useCallback(
    (step: Step, answer: { answers?: unknown; other?: string }) => {
      baseTrack("onboarding_step_answer", {
        step,
        answer: answer.answers,
        ...(answer.other && { answer_other: answer.other }),
      });
    },
    [baseTrack]
  );

  const trackOnboardingCompleted = useCallback(
    (onboardingState: OnboardingState) => {
      const stepKeys: Step[] = [
        "organization",
        "department",
        "usage",
        "business",
        "website",
        "source",
        "finish",
      ];

      const simplifiedAnswers = stepKeys.reduce(
        (acc, step) => {
          const stepValue = onboardingState[step];
          acc[step] =
            typeof stepValue === "object" && stepValue !== null
              ? stepValue.answers
              : stepValue;
          return acc;
        },
        {} as Record<Step, unknown>
      );

      baseTrack("onboarding_complete", { answers: simplifiedAnswers });
    },
    [baseTrack]
  );

  const trackClick = useCallback(
    <T extends keyof AnalyticsEventsMap>({
      eventName,
      clickName,
      clickText,
      eventProps,
    }: {
      eventName?: T;
      clickName?: string;
      clickText?: string;
      eventProps?: AnalyticsEventsMap[T];
    }) => {
      const properties = {
        click_name: clickName,
        click_text: clickText,
        ...eventProps,
      };
      baseTrack(eventName ?? "click", properties);
    },
    [baseTrack]
  );

  const trackEvent = useCallback(
    <T extends keyof AnalyticsEventsMap>(
      eventName: T,
      eventProps: AnalyticsEventsMap[T]
    ) => {
      baseTrack(eventName, eventProps as Record<string, unknown>);
    },
    [baseTrack]
  );

  const trackOnboardingEvent = useCallback(
    <T extends keyof AnalyticsEventsMap>(
      eventName: T,
      additionalProps?: Omit<
        AnalyticsEventsMap[T],
        "plan" | "signup_method" | "billing_interval" | "plan_type"
      >,
      user?: { plan?: Plan }, // TODO remove not used
      signupMethod?: AuthMethod // to override stored onboarding properties
    ) => {
      const onboardingProps = getOnboardingProperties(
        user,
        signupMethod as "email" | "google" | "oauth"
      );
      const props = {
        ...onboardingProps,
        ...additionalProps,
      }; // TODO fix type as AnalyticsEventsMap[T]
      baseTrack(eventName, props as Record<string, unknown>);
    },
    [baseTrack]
  );

  const group = useCallback(
    (groupId: string, traits?: Record<string, unknown>) => {
      baseGroup(groupId, traits);
    },
    [baseGroup]
  );

  return {
    trackPageView,
    identifyUser,
    group,
    trackOnboardingStep,
    trackOnboardingAnswer,
    trackOnboardingCompleted,
    trackClick,
    trackEvent,
    trackOnboardingEvent,
  };
};

/**
 * Fire a typed analytics event exactly once per component mount.
 * Useful for page-view-style events that should not re-fire on re-renders.
 */
export function useTrackOnce<T extends keyof AnalyticsEventsMap>(
  eventName: T,
  eventProps: AnalyticsEventsMap[T]
): void {
  const { trackEvent } = useAnalytics();
  const hasFired = useRef(false);
  const propsRef = useRef(eventProps);

  useEffect(() => {
    propsRef.current = eventProps;
  });

  useEffect(() => {
    if (!hasFired.current) {
      hasFired.current = true;
      trackEvent(eventName, propsRef.current);
    }
  }, [trackEvent, eventName]);
}

export const PageViewTracker: React.FC = () => {
  const location = useLocation();
  const { trackPageView } = useAnalytics();
  const lastTrackedPath = React.useRef<string | null>(null);

  useEffect(() => {
    // Guard: Only track if location actually changed
    // This prevents duplicate tracking during re-renders
    const currentPath = location.pathname + location.search;
    if (lastTrackedPath.current === currentPath) {
      return;
    }

    lastTrackedPath.current = currentPath;
    trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]); // Only depend on location, not trackPageView (already guarded by path check)

  // fallback value if not defined in Route
  return <HtmlPageTitle />;
};

// https://medium.com/@hayata.suenaga/ts-higher-order-components-30c38dd19ae8
type PropsProvidedByHOC = object;

interface PropsHandledByHOC<T extends keyof AnalyticsEventsMap> {
  trackingEventName?: T;
  trackingName?: string;
  trackingText?: string;
  trackingProps?: AnalyticsEventsMap[T];
  children?: ReactNode;
  onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

export const withClickTracking = <
  P extends PropsProvidedByHOC,
  T extends keyof AnalyticsEventsMap = keyof AnalyticsEventsMap,
>(
  WrappedComponent: React.ComponentType<P>
) => {
  return function WithClickTracking(
    props: Omit<P, keyof PropsProvidedByHOC> & PropsHandledByHOC<T>
  ) {
    const {
      trackingEventName,
      trackingName,
      trackingProps,
      trackingText,
      ...restProps
    } = props;
    const { trackClick } = useAnalytics();

    const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      try {
        trackClick({
          eventName: trackingEventName,
          clickName: trackingName,
          clickText: trackingText ?? props.children?.toString(),
          eventProps: trackingProps,
        });
      } catch (e) {
        console.error("Error tracking button click", e);
      }

      if (props.onClick) {
        props.onClick(event);
      }
    };

    return (
      <WrappedComponent
        {...(restProps as unknown as P)}
        onClick={handleClick}
      />
    );
  };
};

export const createUserTraits = (
  user?: Partial<User>,
  additionalProps?: {
    signup_method?: AuthMethod;
    plan?: Plan;
    plan_type?: PlanType;
    billing_interval?: BillingInterval;
  } & {[key: string]: string}
): UserTraits => {
  if (!user && !additionalProps) return {};
  const {signup_method, plan, plan_type, billing_interval, ...restAdditionalProps} = additionalProps ?? {}
  return {
    ...(user?.email && { email: user.email }),
    ...(user?.first_name && { firstName: user.first_name }),
    ...(user?.last_name && { lastName: user.last_name }),
    ...(user?.country && { address: { country: user.country } }),
    ...(user?.created && { createdAt: addDateTz(user.created) }),
    // used to group users by organization in Mixpanel
    ...(user?.organizations?.length && {
      organization_id: user.organizations,
    }),
    ...(additionalProps?.signup_method && {
      signup_method: additionalProps.signup_method,
    }),
    ...(additionalProps?.plan && { plan: additionalProps.plan }),
    ...(additionalProps?.plan_type && { plan_type: additionalProps.plan_type }),
    ...(additionalProps?.billing_interval && {
      billing_interval: additionalProps.billing_interval,
    }),
    ...restAdditionalProps
  };
};

// Export a non-hook version for use outside of React components
export const trackEventWithoutHooks = async <
  T extends keyof AnalyticsEventsMap,
>(
  eventName: T,
  eventProps: AnalyticsEventsMap[T]
) => {
  const analytics = await getAnalytics();
  const trackingProps = getTrackingProperties(
    eventProps as Record<string, unknown>
  );
  await analytics.track(eventName, trackingProps);
};

// Export a non-hook identify for use outside of React components
export const identifyWithoutHooks = async (
  userId: string,
  traits: UserTraits
) => {
  const analytics = await getAnalytics();
  const userTraits = {
    ...traits,
    ...getUserCommonProperties(),
  };

  await analytics.identify(userId, userTraits, {
    context: getTrackingContext(),
  });
};
